import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { Modal, Button, Offcanvas } from "react-bootstrap";
import Select, { MultiValue } from "react-select";
import { createUser, deleteUser, updateUser, updateUserRole, userList } from "./userAPI";
import CollapseHeader from "../../../core/common/collapse-header";
import ResponseModal from "../../../core/modals/response_modal";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';


interface User {
  id: number;
  uuid: string;
  username: string;
  role: string;
  email: string;
  status: string;
}

interface UserDetail {
  username: string;
  password: string;
  confirmPassword: string;
  email: string;
  role: string;
  redirectUrl?: string;
}
const UserList = () => {
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [response, setResponse] = useState<{ status: "success" | "error"; message: string } | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [user, setUser] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    role: "",
    redirectUrl: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    let newErrors: Record<string, string> = {};

    if (!user.username.trim()) newErrors.firstName = "First Name is required";
    if (!user.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) newErrors.email = "Invalid email format";
    if (!user.password.trim()) newErrors.password = "Password is required";
    if (!user.confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirm Password is required";
    } else if (user.password !== user.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (!user.role.trim()) newErrors.role = "Role is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowMessageModal(true);
    try {
      const payload: UserDetail = {
        username: user.username,
        password: user.password,
        confirmPassword: user.confirmPassword,
        email: user.email,
        role: user.role,
        redirectUrl: "https://sinnovar-uat.vercel.app/verify-register",
      };

      const response = await createUser(payload);
      if (response.data.code === 200) {
        setResponse({ status: "success", message: response.data.message || "User created successfully!" });
        setUser({
          username: "",
          password: "",
          confirmPassword: "",
          email: "",
          role: "",
          redirectUrl: ""
        });
        fetchUser();
        setErrors({});
      } else {
        setResponse({ status: "error", message: response.data.message || "User created failed!" });
      }
    } catch (error: any) {
      if (error.response?.data?.code != 500) {
        setResponse({ status: "error", message: error.response.data.message || "Updated failed" });
        setShowMessageModal(true);
      }
    }
  };

  const fetchUser = async () => {
    try {
      const userTenantId = Number(localStorage.getItem("userTenantId"));

      const response = await userList(userTenantId);
      if (response.data.data && response.data.data) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [username, setUsername] = useState<string>("");
  const [currentRole, setCurrentRole] = useState<string>("");
  const [userId, setUserId] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);

  const handleEditUserInfo = (id: number, currentUsername: string, currentRole: string) => {
    setUsername(currentUsername);
    setCurrentRole(currentRole);
    setUserId(id);
    setShowModal(true);
  };

  const handleSaveUserInfo = async () => {
    try {
      setShowMessageModal(true);

      const [usernameRes, roleRes] = await Promise.all([
        updateUser(userId, username),
        updateUserRole(userId, currentRole)
      ]);

      let message = "";
      let isSuccess = true;

      if (usernameRes.status === 200 && roleRes.status === 200) {
        message = "Update successfully. ";
      } else {
        message = "Error updating. ";
        isSuccess = false;
      }

      setResponse({
        status: isSuccess ? "success" : "error",
        message
      });

      if (isSuccess) {
        fetchUser();
        setShowModal(false);
      }

    } catch (error) {
      console.error(error);
      setResponse({ status: "error", message: "Something went wrong!" });
    }
  };


  const [userIdToDelete, setUserIdToDelete] = useState<number>();
  const handleDeleteUser = async (id: number) => {
    setUserIdToDelete(id);
  };

  const handleConfirmDelete = async () => {
    try {
      setShowMessageModal(true);
      const response = await deleteUser(userIdToDelete);
      if (response.status === 200) {
        fetchUser();
        setResponse({ status: "success", message: "User deleted successfully!" });
      } else {
        setResponse({ status: "error", message: "Error deleteting user!" });
      }
    } catch (error) {
      console.error(error);
      setResponse({ status: "error", message: "Error deleteting user!" });
    }
  };


  const columns = [
    {
      title: "User Name",
      dataIndex: "username",
      sorter: (a: User, b: User) => a.username.length - b.username.length,
    },
    {
      title: "Role",
      dataIndex: "role",
      sorter: (a: User, b: User) =>
        a.role.length - b.role.length,
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a: User, b: User) =>
        a.email.length - b.email.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a: User, b: User) =>
        a.status.length - b.status.length,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: User) => (
        <div className="dropdown table-action">
          <Link to="#" className="action-icon" data-bs-toggle="dropdown">
            <i className="fa fa-ellipsis-v"></i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <button
              className="dropdown-item"
              onClick={() => handleEditUserInfo(record.id, record.username, record.role)}
            >
              <i className="ti ti-edit text-blue"></i> Edit User Info
            </button>

            {/* Delete Button */}
            <button
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_user_modal"
              onClick={() => handleDeleteUser(record.id)}
            >
              <i className="ti ti-trash text-danger"></i> Delete
            </button>
          </div>
        </div>
      ),
    }
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      User<span className="count-title">{users.length}</span>
                    </h4>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              <div className="card">
                <div className="card-body">
                  <>
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                      </div>
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_user"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add User
                        </Link>
                      </div>
                    </div>
                  </>
                  {/* user List */}
                  <div className="table-responsive custom-table">
                    <Table dataSource={users} columns={columns} />
                  </div>
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="datatable-length" />
                    </div>
                    <div className="col-md-6">
                      <div className="datatable-paginate" />
                    </div>
                  </div>
                  {/* /user List */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Create New User */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_user"
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Add New User</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>

        <div className="offcanvas-body">
          <form>
            <div className="accordion" id="user_accordion">
              <div className="accordion-item rounded mb-3">
                <div className="accordion-collapse collapse show" id="user_basic" data-bs-parent="#user_accordion">
                  <div className="accordion-body border-top">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Username <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            name="username"
                            value={user.username}
                            onChange={handleInputChange}
                          />
                          {errors.username && <small className="text-danger">{errors.username}</small>}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Email <span className="text-danger">*</span></label>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={user.email}
                            onChange={handleInputChange}
                          />
                          {errors.email && <small className="text-danger">{errors.email}</small>}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Password <span className="text-danger">*</span></label>
                          <input
                            type="password"
                            className="form-control"
                            name="password"
                            value={user.password}
                            onChange={handleInputChange}
                          />
                          {errors.password && <small className="text-danger">{errors.password}</small>}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Confirm Password <span className="text-danger">*</span></label>
                          <input
                            type="password"
                            className="form-control"
                            name="confirmPassword"
                            value={user.confirmPassword}
                            onChange={handleInputChange}
                          />
                          {errors.confirmPassword && <small className="text-danger">{errors.confirmPassword}</small>}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Role <span className="text-danger">*</span></label>
                          <select className="form-select" name="role" value={user.role} onChange={handleInputChange}>
                            <option value="" disabled>Select Role</option>
                            <option value="MARKETING">MARKETING</option>
                            <option value="SALE_MANAGER">SALE_MANAGER</option>
                            <option value="SDR">SDR</option>
                            <option value="ACCOUNT_EXECUTIVE">ACCOUNT_EXECUTIVE</option>
                          </select>
                          {errors.role && <small className="text-danger">{errors.role}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-end">
              <button className="btn btn-primary" onClick={handleSubmit}>
                Create User
              </button>
            </div>
          </form>
          <ResponseModal show={showMessageModal} onClose={() => setShowMessageModal(false)} response={response} />
        </div>
      </div>
      {/* Edit User */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Edit User Info</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {/* Username */}
                <div className="mb-3">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>

                {/* Role */}
                <div className="mb-3">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={currentRole}
                    onChange={(e) => setCurrentRole(e.target.value)}
                  >
                    <option value="" disabled>Select Role</option>
                    <option value="MARKETING">MARKETING</option>
                    <option value="SALE_MANAGER">SALE_MANAGER</option>
                    <option value="SDR">SDR</option>
                    <option value="ACCOUNT_EXECUTIVE">ACCOUNT_EXECUTIVE</option>
                  </select>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={handleSaveUserInfo}>Save changes</button>
                </div>

                <ResponseModal show={showMessageModal} onClose={() => setShowMessageModal(false)} response={response} />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete User */}
      <div className="modal fade" id="delete_user_modal" tabIndex={-1} aria-labelledby="delete_user_modalLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="delete_user_modalLabel">Delete User</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this user?
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Delete</button>
            </div>
            <ResponseModal show={showMessageModal} onClose={() => setShowMessageModal(false)} response={response} />
          </div>
        </div>
      </div>
    </>
  )
};
export default UserList;
